import { collection, doc, setDoc, deleteDoc, query, onSnapshot, orderBy, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { perfTracker } from './perfLogger';
import { Product } from '../types';
import { slugify } from './slugBase';

export interface CountryModel {
  id: string;
  name: string;
  slug: string;
  iso_code: string;
  flag: string;
  description: string;
  seo_title?: string;
  seo_description?: string;
  status: 'active' | 'hidden';
  display_order: number;
  is_featured?: boolean;
  keywords?: string[];
  aliases?: string[];
  productCount?: number;
  created_at?: string;
  updated_at?: string;
}

export const COUNTRIES_COLLECTION = 'countries';

export const INITIAL_COUNTRIES: CountryModel[] = [
  {
    "id": "us",
    "name": "United States",
    "slug": "united-states",
    "iso_code": "us",
    "flag": "https://flagcdn.com/w160/us.png",
    "description": "High quality United States editable passport, driver license, state ID, and utility bill PSD templates.",
    "seo_title": "United States PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download high-resolution USA passport, driver license, SSN, and state ID editable PSD templates with smart object layers.",
    "status": "active",
    "display_order": 1,
    "is_featured": true,
    "keywords": [
      "usa",
      "us",
      "united states",
      "america",
      "american",
      "dl",
      "passport",
      "state id",
      "ssn"
    ],
    "aliases": [
      "usa",
      "us",
      "america",
      "american",
      "u.s.a.",
      "u.s."
    ]
  },
  {
    "id": "gb",
    "name": "United Kingdom",
    "slug": "united-kingdom",
    "iso_code": "gb",
    "flag": "https://flagcdn.com/w160/gb.png",
    "description": "Official United Kingdom UK driving licence, passport, council tax bill, and bank statement PSD vector templates.",
    "seo_title": "United Kingdom UK PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Editable British UK driving licence, passport, utility bill, and bank statement PSD templates with smart fonts and vectors.",
    "status": "active",
    "display_order": 2,
    "is_featured": true,
    "keywords": [
      "uk",
      "gb",
      "united kingdom",
      "britain",
      "british",
      "england",
      "scotland",
      "wales",
      "london"
    ],
    "aliases": [
      "uk",
      "gb",
      "britain",
      "british",
      "england",
      "u.k."
    ]
  },
  {
    "id": "ca",
    "name": "Canada",
    "slug": "canada",
    "iso_code": "ca",
    "flag": "https://flagcdn.com/w160/ca.png",
    "description": "Canadian biometric passport, provincial driver licenses (Ontario, BC, Quebec), and utility bill PSD templates.",
    "seo_title": "Canada PSD Templates & Editable Passport & ID Files | RH Editography",
    "seo_description": "High-quality editable Canadian passport, driver license, SIN card, and utility bill PSD templates.",
    "status": "active",
    "display_order": 3,
    "is_featured": true,
    "keywords": [
      "canada",
      "ca",
      "canadian",
      "ontario",
      "quebec",
      "bc",
      "sin",
      "toronto"
    ],
    "aliases": [
      "ca",
      "canadian",
      "can"
    ]
  },
  {
    "id": "au",
    "name": "Australia",
    "slug": "australia",
    "iso_code": "au",
    "flag": "https://flagcdn.com/w160/au.png",
    "description": "Australian passport, NSW / VIC driver licenses, Medicare cards, and utility bill PSD vector templates.",
    "seo_title": "Australia PSD Templates & Driver License ID Files | RH Editography",
    "seo_description": "Download editable Australian passport, driver licence, Medicare, and bank statement PSD templates.",
    "status": "active",
    "display_order": 4,
    "is_featured": true,
    "keywords": [
      "australia",
      "au",
      "australian",
      "aussie",
      "nsw",
      "vic",
      "medicare",
      "sydney"
    ],
    "aliases": [
      "au",
      "australian",
      "aussie"
    ]
  },
  {
    "id": "de",
    "name": "Germany",
    "slug": "germany",
    "iso_code": "de",
    "flag": "https://flagcdn.com/w160/de.png",
    "description": "German Reisepass, Personalausweis ID, Führerschein driver license, and utility bill PSD templates.",
    "seo_title": "Germany PSD Templates - Personalausweis & Reisepass | RH Editography",
    "seo_description": "Download editable German passport (Reisepass), ID card (Personalausweis), and Führerschein PSD files.",
    "status": "active",
    "display_order": 6,
    "is_featured": true,
    "keywords": [
      "germany",
      "de",
      "german",
      "deutschland",
      "reisepass",
      "personalausweis",
      "führerschein"
    ],
    "aliases": [
      "de",
      "german",
      "deutschland",
      "ger"
    ]
  },
  {
    "id": "fr",
    "name": "France",
    "slug": "france",
    "iso_code": "fr",
    "flag": "https://flagcdn.com/w160/fr.png",
    "description": "French passport, Carte Nationale d Identity, Permis de Conduire driving license PSD templates.",
    "seo_title": "France PSD Templates & French ID & Passport Files | RH Editography",
    "seo_description": "Editable French passport, identity card, driver license, and utility bill PSD templates.",
    "status": "active",
    "display_order": 7,
    "is_featured": true,
    "keywords": [
      "france",
      "fr",
      "french",
      "carte d identite",
      "permis",
      "paris"
    ],
    "aliases": [
      "fr",
      "french",
      "fra"
    ]
  },
  {
    "id": "in",
    "name": "India",
    "slug": "india",
    "iso_code": "in",
    "flag": "https://flagcdn.com/w160/in.png",
    "description": "Indian Aadhaar Card, PAN Card, Passport, Driving Licence, and Voter ID PSD templates.",
    "seo_title": "India Aadhaar & PAN Card PSD Templates | RH Editography",
    "seo_description": "Editable Indian Aadhaar card, PAN card, Passport, driving licence, and utility bill PSD files.",
    "status": "active",
    "display_order": 8,
    "is_featured": true,
    "keywords": [
      "india",
      "in",
      "indian",
      "aadhaar",
      "pan",
      "voter id",
      "delhi"
    ],
    "aliases": [
      "in",
      "indian",
      "ind"
    ]
  },
  {
    "id": "jp",
    "name": "Japan",
    "slug": "japan",
    "iso_code": "jp",
    "flag": "https://flagcdn.com/w160/jp.png",
    "description": "Japanese biometric passport, My Number card, driver license, and residence card PSD templates.",
    "seo_title": "Japan Passport & My Number Card PSD Templates | RH Editography",
    "seo_description": "Editable Japan passport, My Number identity card, driver license, and utility bill PSD templates.",
    "status": "active",
    "display_order": 9,
    "is_featured": false,
    "keywords": [
      "japan",
      "jp",
      "japanese",
      "tokyo",
      "my number",
      "gaijin card"
    ],
    "aliases": [
      "jp",
      "japanese",
      "jpn"
    ]
  },
  {
    "id": "ru",
    "name": "Russia",
    "slug": "russia",
    "iso_code": "ru",
    "flag": "https://flagcdn.com/w160/ru.png",
    "description": "Russian Federation internal & international passport, driver license, and SNILS PSD templates.",
    "seo_title": "Russia Passport & Driver License PSD Templates | RH Editography",
    "seo_description": "High resolution editable Russian passport, driver license, and utility bill PSD files.",
    "status": "active",
    "display_order": 10,
    "is_featured": true,
    "keywords": [
      "russia",
      "ru",
      "russian",
      "moscow",
      "snils",
      "passport"
    ],
    "aliases": [
      "ru",
      "russian",
      "rus"
    ]
  },
  {
    "id": "pl",
    "name": "Poland",
    "slug": "poland",
    "iso_code": "pl",
    "flag": "https://flagcdn.com/w160/pl.png",
    "description": "Polish Dowod Osobisty ID card, passport, Prawo Jazdy driver license PSD templates.",
    "seo_title": "Poland PSD Templates - Dowod Osobisty & Passport | RH Editography",
    "seo_description": "Download editable Polish ID card (Dowód Osobisty), passport, and driver licence PSD files.",
    "status": "active",
    "display_order": 11,
    "keywords": [
      "poland",
      "pl",
      "polish",
      "dowod",
      "prawo jazdy"
    ],
    "aliases": [
      "pl",
      "polish",
      "pol"
    ]
  },
  {
    "id": "ua",
    "name": "Ukraine",
    "slug": "ukraine",
    "iso_code": "ua",
    "flag": "https://flagcdn.com/w160/ua.png",
    "description": "Ukrainian ID card, passport, driver license, and utility bill editable PSD vector templates.",
    "seo_title": "Ukraine Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Ukraine passport, ID card, driving license, and bank statement PSD templates.",
    "status": "active",
    "display_order": 12,
    "keywords": [
      "ukraine",
      "ua",
      "ukrainian",
      "kyiv"
    ],
    "aliases": [
      "ua",
      "ukrainian",
      "ukr"
    ]
  },
  {
    "id": "ro",
    "name": "Romania",
    "slug": "romania",
    "iso_code": "ro",
    "flag": "https://flagcdn.com/w160/ro.png",
    "description": "Romanian Carte de Identitate ID card, passport, and Permis de Conducere PSD templates.",
    "seo_title": "Romania ID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Download editable Romanian passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 13,
    "keywords": [
      "romania",
      "ro",
      "romanian",
      "bucharest"
    ],
    "aliases": [
      "ro",
      "romanian",
      "rom"
    ]
  },
  {
    "id": "es",
    "name": "Spain",
    "slug": "spain",
    "iso_code": "es",
    "flag": "https://flagcdn.com/w160/es.png",
    "description": "Spanish DNI Documento Nacional de Identidad, NIE, Pasaporte, and License PSD templates.",
    "seo_title": "Spain DNI & NIE PSD Templates | RH Editography",
    "seo_description": "Editable Spanish DNI, NIE card, Pasaporte, and driver license PSD templates.",
    "status": "active",
    "display_order": 14,
    "keywords": [
      "spain",
      "es",
      "spanish",
      "dni",
      "nie",
      "pasaporte"
    ],
    "aliases": [
      "es",
      "spanish",
      "esp"
    ]
  },
  {
    "id": "it",
    "name": "Italy",
    "slug": "italy",
    "iso_code": "it",
    "flag": "https://flagcdn.com/w160/it.png",
    "description": "Italian Carta d Identita electronic ID, passport, and Patente di Guida PSD templates.",
    "seo_title": "Italy Carta d Identita & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Italian electronic ID card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 15,
    "keywords": [
      "italy",
      "it",
      "italian",
      "carta identita",
      "patente",
      "rome"
    ],
    "aliases": [
      "it",
      "italian",
      "ita"
    ]
  },
  {
    "id": "nl",
    "name": "Netherlands",
    "slug": "netherlands",
    "iso_code": "nl",
    "flag": "https://flagcdn.com/w160/nl.png",
    "description": "Dutch Identiteitskaart ID card, passport, and Rijbewijs driver license PSD templates.",
    "seo_title": "Netherlands Dutch Passport & ID PSD Templates | RH Editography",
    "seo_description": "Download editable Dutch passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 16,
    "keywords": [
      "netherlands",
      "nl",
      "dutch",
      "holland",
      "rijbewijs",
      "amsterdam"
    ],
    "aliases": [
      "nl",
      "dutch",
      "holland",
      "nld"
    ]
  },
  {
    "id": "at",
    "name": "Austria",
    "slug": "austria",
    "iso_code": "at",
    "flag": "https://flagcdn.com/w160/at.png",
    "description": "Austrian Personalausweis, Reisepass, and Führerschein driver license PSD templates.",
    "seo_title": "Austria Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Austrian passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 17,
    "keywords": [
      "austria",
      "at",
      "austrian",
      "vienna"
    ],
    "aliases": [
      "at",
      "austrian",
      "aut"
    ]
  },
  {
    "id": "ee",
    "name": "Estonia",
    "slug": "estonia",
    "iso_code": "ee",
    "flag": "https://flagcdn.com/w160/ee.png",
    "description": "Estonian e-Residency card, ID card, and driver license editable PSD templates.",
    "seo_title": "Estonia e-Residency & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Estonian e-Residency digital ID, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 18,
    "keywords": [
      "estonia",
      "ee",
      "estonian",
      "e-residency",
      "tallinn"
    ],
    "aliases": [
      "ee",
      "estonian",
      "est"
    ]
  },
  {
    "id": "lt",
    "name": "Lithuania",
    "slug": "lithuania",
    "iso_code": "lt",
    "flag": "https://flagcdn.com/w160/lt.png",
    "description": "Lithuanian Asmens Tapatybes Kortele ID, passport, and driver license PSD templates.",
    "seo_title": "Lithuania ID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Download editable Lithuanian passport and ID card PSD files.",
    "status": "active",
    "display_order": 19,
    "keywords": [
      "lithuania",
      "lt",
      "lithuanian",
      "vilnius"
    ],
    "aliases": [
      "lt",
      "lithuanian",
      "ltu"
    ]
  },
  {
    "id": "lv",
    "name": "Latvia",
    "slug": "latvia",
    "iso_code": "lv",
    "flag": "https://flagcdn.com/w160/lv.png",
    "description": "Latvian eID card, passport, and driver license editable PSD templates.",
    "seo_title": "Latvia Passport & eID Card PSD Templates | RH Editography",
    "seo_description": "Editable Latvian passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 20,
    "keywords": [
      "latvia",
      "lv",
      "latvian",
      "riga"
    ],
    "aliases": [
      "lv",
      "latvian",
      "lva"
    ]
  },
  {
    "id": "cz",
    "name": "Czechia",
    "slug": "czechia",
    "iso_code": "cz",
    "flag": "https://flagcdn.com/w160/cz.png",
    "description": "Czech Republic Obcansky prukaz ID, passport, and Ridicsky prukaz PSD templates.",
    "seo_title": "Czechia ID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Czech Republic ID card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 21,
    "keywords": [
      "czechia",
      "cz",
      "czech",
      "czech republic",
      "prague"
    ],
    "aliases": [
      "cz",
      "czech",
      "czech republic",
      "cze"
    ]
  },
  {
    "id": "se",
    "name": "Sweden",
    "slug": "sweden",
    "iso_code": "se",
    "flag": "https://flagcdn.com/w160/se.png",
    "description": "Swedish Nationellt identitetskort ID, passport, and Korkort license PSD templates.",
    "seo_title": "Sweden Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Swedish passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 22,
    "keywords": [
      "sweden",
      "se",
      "swedish",
      "korkort",
      "stockholm"
    ],
    "aliases": [
      "se",
      "swedish",
      "swe"
    ]
  },
  {
    "id": "no",
    "name": "Norway",
    "slug": "norway",
    "iso_code": "no",
    "flag": "https://flagcdn.com/w160/no.png",
    "description": "Norwegian Nasjonalt id-kort ID, passport, and Forerkort license PSD templates.",
    "seo_title": "Norway Passport & Driver License PSD Templates | RH Editography",
    "seo_description": "Editable Norwegian passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 23,
    "keywords": [
      "norway",
      "no",
      "norwegian",
      "oslo"
    ],
    "aliases": [
      "no",
      "norwegian",
      "nor"
    ]
  },
  {
    "id": "ie",
    "name": "Ireland",
    "slug": "ireland",
    "iso_code": "ie",
    "flag": "https://flagcdn.com/w160/ie.png",
    "description": "Irish Passport Card, passport booklet, driving licence, and PPS card PSD templates.",
    "seo_title": "Ireland Passport & Driver Licence PSD Templates | RH Editography",
    "seo_description": "Download editable Irish passport, ID card, and driving licence PSD files.",
    "status": "active",
    "display_order": 24,
    "keywords": [
      "ireland",
      "ie",
      "irish",
      "dublin"
    ],
    "aliases": [
      "ie",
      "irish",
      "irl"
    ]
  },
  {
    "id": "fi",
    "name": "Finland",
    "slug": "finland",
    "iso_code": "fi",
    "flag": "https://flagcdn.com/w160/fi.png",
    "description": "Finnish Henkilokortti ID, passport, and Ajokortti driver license PSD templates.",
    "seo_title": "Finland Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Finnish passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 25,
    "keywords": [
      "finland",
      "fi",
      "finnish",
      "helsinki"
    ],
    "aliases": [
      "fi",
      "finnish",
      "fin"
    ]
  },
  {
    "id": "ch",
    "name": "Switzerland",
    "slug": "switzerland",
    "iso_code": "ch",
    "flag": "https://flagcdn.com/w160/ch.png",
    "description": "Swiss Identitatskarte ID, passport, and Permis de conduire license PSD templates.",
    "seo_title": "Switzerland Swiss Passport & ID PSD Templates | RH Editography",
    "seo_description": "Editable Swiss passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 26,
    "keywords": [
      "switzerland",
      "ch",
      "swiss",
      "zurich",
      "geneva"
    ],
    "aliases": [
      "ch",
      "swiss",
      "che"
    ]
  },
  {
    "id": "be",
    "name": "Belgium",
    "slug": "belgium",
    "iso_code": "be",
    "flag": "https://flagcdn.com/w160/be.png",
    "description": "Belgian eID card, passport, and Rijbewijs driver license PSD templates.",
    "seo_title": "Belgium Passport & eID Card PSD Templates | RH Editography",
    "seo_description": "Editable Belgian eID, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 27,
    "keywords": [
      "belgium",
      "be",
      "belgian",
      "brussels"
    ],
    "aliases": [
      "be",
      "belgian",
      "bel"
    ]
  },
  {
    "id": "ae",
    "name": "United Arab Emirates",
    "slug": "united-arab-emirates",
    "iso_code": "ae",
    "flag": "https://flagcdn.com/w160/ae.png",
    "description": "UAE Emirates ID card, passport, Dubai driver license, and DEWA bill PSD templates.",
    "seo_title": "UAE Emirates ID & Dubai Driver License PSD Templates | RH Editography",
    "seo_description": "Editable UAE Emirates ID card, passport, Dubai driver license, and DEWA utility bill PSD files.",
    "status": "active",
    "display_order": 28,
    "is_featured": true,
    "keywords": [
      "uae",
      "ae",
      "dubai",
      "emirates",
      "abu dhabi",
      "emirates id"
    ],
    "aliases": [
      "uae",
      "ae",
      "dubai",
      "emirates",
      "united arab emirates"
    ]
  },
  {
    "id": "tr",
    "name": "Turkey",
    "slug": "turkey",
    "iso_code": "tr",
    "flag": "https://flagcdn.com/w160/tr.png",
    "description": "Turkish Kimlik ID card, passport, and Driver License PSD templates.",
    "seo_title": "Turkey Kimlik ID & Passport PSD Templates | RH Editography",
    "seo_description": "Download editable Turkish Kimlik identity card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 29,
    "keywords": [
      "turkey",
      "tr",
      "turkish",
      "kimlik",
      "istanbul",
      "turkiye"
    ],
    "aliases": [
      "tr",
      "turkish",
      "turkiye",
      "tur"
    ]
  },
  {
    "id": "br",
    "name": "Brazil",
    "slug": "brazil",
    "iso_code": "br",
    "flag": "https://flagcdn.com/w160/br.png",
    "description": "Brazilian RG Identity card, CPF, Passaporte, and CNH driver license PSD templates.",
    "seo_title": "Brazil RG & CNH Driver License PSD Templates | RH Editography",
    "seo_description": "Editable Brazilian RG ID card, CPF, Passaporte, and CNH driver license PSD files.",
    "status": "active",
    "display_order": 30,
    "keywords": [
      "brazil",
      "br",
      "brazilian",
      "rg",
      "cnh",
      "cpf"
    ],
    "aliases": [
      "br",
      "brazilian",
      "bra"
    ]
  },
  {
    "id": "ar",
    "name": "Argentina",
    "slug": "argentina",
    "iso_code": "ar",
    "flag": "https://flagcdn.com/w160/ar.png",
    "description": "Argentine DNI Documento Nacional de Identidad, passport, and Licencia de Conducir PSD templates.",
    "seo_title": "Argentina DNI & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Argentine DNI identity card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 31,
    "keywords": [
      "argentina",
      "ar",
      "argentine",
      "dni",
      "buenos aires"
    ],
    "aliases": [
      "ar",
      "argentine",
      "arg"
    ]
  },
  {
    "id": "mx",
    "name": "Mexico",
    "slug": "mexico",
    "iso_code": "mx",
    "flag": "https://flagcdn.com/w160/mx.png",
    "description": "Mexican INE / IFE voter ID card, passport, CURP, and Licencia de Conducir PSD templates.",
    "seo_title": "Mexico INE ID & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Mexican INE voter ID card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 32,
    "keywords": [
      "mexico",
      "mx",
      "mexican",
      "ine",
      "ife",
      "curp"
    ],
    "aliases": [
      "mx",
      "mexican",
      "mex"
    ]
  },
  {
    "id": "cn",
    "name": "China",
    "slug": "china",
    "iso_code": "cn",
    "flag": "https://flagcdn.com/w160/cn.png",
    "description": "Chinese Resident Identity Card, passport, and driver license PSD templates.",
    "seo_title": "China Resident ID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Download editable Chinese identity card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 33,
    "keywords": [
      "china",
      "cn",
      "chinese",
      "shenfenzheng",
      "beijing"
    ],
    "aliases": [
      "cn",
      "chinese",
      "chn"
    ]
  },
  {
    "id": "sg",
    "name": "Singapore",
    "slug": "singapore",
    "iso_code": "sg",
    "flag": "https://flagcdn.com/w160/sg.png",
    "description": "Singapore NRIC / FIN card, passport, and driving licence PSD templates.",
    "seo_title": "Singapore NRIC & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Singapore NRIC card, passport, and driving licence PSD templates.",
    "status": "active",
    "display_order": 34,
    "keywords": [
      "singapore",
      "sg",
      "singaporean",
      "nric",
      "fin"
    ],
    "aliases": [
      "sg",
      "singaporean",
      "sgp"
    ]
  },
  {
    "id": "za",
    "name": "South Africa",
    "slug": "south-africa",
    "iso_code": "za",
    "flag": "https://flagcdn.com/w160/za.png",
    "description": "South African Smart ID card, passport, driving licence, and utility bill PSD templates.",
    "seo_title": "South Africa Smart ID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable South African Smart ID card, passport, and driving licence PSD files.",
    "status": "active",
    "display_order": 35,
    "keywords": [
      "south africa",
      "za",
      "south african",
      "smart id"
    ],
    "aliases": [
      "za",
      "south african",
      "zaf"
    ]
  },
  {
    "id": "nz",
    "name": "New Zealand",
    "slug": "new-zealand",
    "iso_code": "nz",
    "flag": "https://flagcdn.com/w160/nz.png",
    "description": "New Zealand passport, driver licence, 18+ card, and utility bill PSD templates.",
    "seo_title": "New Zealand Passport & Driver Licence PSD Templates | RH Editography",
    "seo_description": "Editable New Zealand passport, driver licence, and utility bill PSD templates.",
    "status": "active",
    "display_order": 36,
    "keywords": [
      "new zealand",
      "nz",
      "kiwi",
      "auckland"
    ],
    "aliases": [
      "nz",
      "kiwi",
      "nzl"
    ]
  },
  {
    "id": "ph",
    "name": "Philippines",
    "slug": "philippines",
    "iso_code": "ph",
    "flag": "https://flagcdn.com/w160/ph.png",
    "description": "Philippine PhilID National ID, UMID card, passport, and LTO driver license PSD templates.",
    "seo_title": "Philippines PhilID & LTO License PSD Templates | RH Editography",
    "seo_description": "Editable Philippine PhilID, UMID, passport, and LTO driver license PSD files.",
    "status": "active",
    "display_order": 37,
    "keywords": [
      "philippines",
      "ph",
      "filipino",
      "philid",
      "umid",
      "lto",
      "manila"
    ],
    "aliases": [
      "ph",
      "filipino",
      "phl"
    ]
  },
  {
    "id": "pk",
    "name": "Pakistan",
    "slug": "pakistan",
    "iso_code": "pk",
    "flag": "https://flagcdn.com/w160/pk.png",
    "description": "Pakistan NADRA Smart CNIC card, passport, driving license, and utility bill PSD templates.",
    "seo_title": "Pakistan NADRA Smart CNIC & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Pakistan NADRA CNIC, passport, driving license, and utility bill PSD templates.",
    "status": "active",
    "display_order": 38,
    "keywords": [
      "pakistan",
      "pk",
      "pakistani",
      "cnic",
      "nadra",
      "islamabad"
    ],
    "aliases": [
      "pk",
      "pakistani",
      "pak"
    ]
  },
  {
    "id": "kz",
    "name": "Kazakhstan",
    "slug": "kazakhstan",
    "iso_code": "kz",
    "flag": "https://flagcdn.com/w160/kz.png",
    "description": "Kazakhstan Identity Card, passport, and driver license editable PSD templates.",
    "seo_title": "Kazakhstan Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Download editable Kazakhstan passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 39,
    "keywords": [
      "kazakhstan",
      "kz",
      "kazakh",
      "astana",
      "almaty"
    ],
    "aliases": [
      "kz",
      "kazakh",
      "kaz"
    ]
  },
  {
    "id": "md",
    "name": "Moldova",
    "slug": "moldova",
    "iso_code": "md",
    "flag": "https://flagcdn.com/w160/md.png",
    "description": "Moldovan Buletin de identitate ID, passport, and Permis de conducere PSD templates.",
    "seo_title": "Moldova Passport & Buletin ID PSD Templates | RH Editography",
    "seo_description": "Editable Moldovan passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 40,
    "keywords": [
      "moldova",
      "md",
      "moldovan",
      "chisinau"
    ],
    "aliases": [
      "md",
      "moldovan",
      "mda"
    ]
  },
  {
    "id": "ge",
    "name": "Georgia",
    "slug": "georgia",
    "iso_code": "ge",
    "flag": "https://flagcdn.com/w160/ge.png",
    "description": "Georgian ID card, passport, and driver license editable PSD templates.",
    "seo_title": "Georgia Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Georgian passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 41,
    "keywords": [
      "georgia",
      "ge",
      "georgian",
      "tbilisi"
    ],
    "aliases": [
      "ge",
      "georgian",
      "geo"
    ]
  },
  {
    "id": "am",
    "name": "Armenia",
    "slug": "armenia",
    "iso_code": "am",
    "flag": "https://flagcdn.com/w160/am.png",
    "description": "Armenian ID card, passport, and driver license editable PSD templates.",
    "seo_title": "Armenia Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Armenian passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 42,
    "keywords": [
      "armenia",
      "am",
      "armenian",
      "yerevan"
    ],
    "aliases": [
      "am",
      "armenian",
      "arm"
    ]
  },
  {
    "id": "az",
    "name": "Azerbaijan",
    "slug": "azerbaijan",
    "iso_code": "az",
    "flag": "https://flagcdn.com/w160/az.png",
    "description": "Azerbaijani ID card, passport, and driver license editable PSD templates.",
    "seo_title": "Azerbaijan Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Azerbaijani passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 43,
    "keywords": [
      "azerbaijan",
      "az",
      "azerbaijani",
      "baku"
    ],
    "aliases": [
      "az",
      "azerbaijani",
      "aze"
    ]
  },
  {
    "id": "bg",
    "name": "Bulgaria",
    "slug": "bulgaria",
    "iso_code": "bg",
    "flag": "https://flagcdn.com/w160/bg.png",
    "description": "Bulgarian Lichna karta ID card, passport, and driver license PSD templates.",
    "seo_title": "Bulgaria Passport & Lichna Karta PSD Templates | RH Editography",
    "seo_description": "Editable Bulgarian passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 44,
    "keywords": [
      "bulgaria",
      "bg",
      "bulgarian",
      "sofia"
    ],
    "aliases": [
      "bg",
      "bulgarian",
      "bgr"
    ]
  },
  {
    "id": "hr",
    "name": "Croatia",
    "slug": "croatia",
    "iso_code": "hr",
    "flag": "https://flagcdn.com/w160/hr.png",
    "description": "Croatian Osobna iskaznica ID card, passport, and Vozacka dozvola PSD templates.",
    "seo_title": "Croatia Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Download editable Croatian passport and ID card PSD files.",
    "status": "active",
    "display_order": 45,
    "keywords": [
      "croatia",
      "hr",
      "croatian",
      "zagreb"
    ],
    "aliases": [
      "hr",
      "croatian",
      "hrv"
    ]
  },
  {
    "id": "cy",
    "name": "Cyprus",
    "slug": "cyprus",
    "iso_code": "cy",
    "flag": "https://flagcdn.com/w160/cy.png",
    "description": "Cypriot ID card, passport, and driving licence PSD templates.",
    "seo_title": "Cyprus Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Cypriot passport, ID card, and driving licence PSD files.",
    "status": "active",
    "display_order": 46,
    "keywords": [
      "cyprus",
      "cy",
      "cypriot",
      "nicosia"
    ],
    "aliases": [
      "cy",
      "cypriot",
      "cyp"
    ]
  },
  {
    "id": "dk",
    "name": "Denmark",
    "slug": "denmark",
    "iso_code": "dk",
    "flag": "https://flagcdn.com/w160/dk.png",
    "description": "Danish Sygesikringskort, passport, and Korekort driver license PSD templates.",
    "seo_title": "Denmark Passport & Driver License PSD Templates | RH Editography",
    "seo_description": "Editable Danish passport, health card, and driver license PSD templates.",
    "status": "active",
    "display_order": 47,
    "keywords": [
      "denmark",
      "dk",
      "danish",
      "copenhagen"
    ],
    "aliases": [
      "dk",
      "danish",
      "dnk"
    ]
  },
  {
    "id": "gr",
    "name": "Greece",
    "slug": "greece",
    "iso_code": "gr",
    "flag": "https://flagcdn.com/w160/gr.png",
    "description": "Greek Identity Card, passport, and driving licence editable PSD templates.",
    "seo_title": "Greece Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Greek passport, identity card, and driver license PSD files.",
    "status": "active",
    "display_order": 48,
    "keywords": [
      "greece",
      "gr",
      "greek",
      "athens"
    ],
    "aliases": [
      "gr",
      "greek",
      "grc"
    ]
  },
  {
    "id": "hu",
    "name": "Hungary",
    "slug": "hungary",
    "iso_code": "hu",
    "flag": "https://flagcdn.com/w160/hu.png",
    "description": "Hungarian Szemelyazonosito igazolvany ID, passport, and Vezetoi engedely PSD templates.",
    "seo_title": "Hungary Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Hungarian passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 49,
    "keywords": [
      "hungary",
      "hu",
      "hungarian",
      "budapest"
    ],
    "aliases": [
      "hu",
      "hungarian",
      "hun"
    ]
  },
  {
    "id": "pt",
    "name": "Portugal",
    "slug": "portugal",
    "iso_code": "pt",
    "flag": "https://flagcdn.com/w160/pt.png",
    "description": "Portuguese Cartao de Cidadao ID, passport, and Carta de Conducao PSD templates.",
    "seo_title": "Portugal Cartao de Cidadao & Passport PSD Templates | RH Editography",
    "seo_description": "Download editable Portuguese Citizen Card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 50,
    "keywords": [
      "portugal",
      "pt",
      "portuguese",
      "cartao cidadao",
      "lisbon"
    ],
    "aliases": [
      "pt",
      "portuguese",
      "prt"
    ]
  },
  {
    "id": "sk",
    "name": "Slovakia",
    "slug": "slovakia",
    "iso_code": "sk",
    "flag": "https://flagcdn.com/w160/sk.png",
    "description": "Slovak Obciansky preukaz ID card, passport, and Vodicaky preukaz PSD templates.",
    "seo_title": "Slovakia Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Slovak passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 51,
    "keywords": [
      "slovakia",
      "sk",
      "slovak",
      "bratislava"
    ],
    "aliases": [
      "sk",
      "slovak",
      "svk"
    ]
  },
  {
    "id": "si",
    "name": "Slovenia",
    "slug": "slovenia",
    "iso_code": "si",
    "flag": "https://flagcdn.com/w160/si.png",
    "description": "Slovenian Osebna izkaznica ID card, passport, and Voznisko dovoljenje PSD templates.",
    "seo_title": "Slovenia Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Slovenian passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 52,
    "keywords": [
      "slovenia",
      "si",
      "slovenian",
      "ljubljana"
    ],
    "aliases": [
      "si",
      "slovenian",
      "svn"
    ]
  },
  {
    "id": "cm",
    "name": "Cameroon",
    "slug": "cameroon",
    "iso_code": "cm",
    "flag": "https://flagcdn.com/w160/cm.png",
    "description": "Cameroonian CNI ID card, passport, and driver license PSD templates.",
    "seo_title": "Cameroon Passport & CNI ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Cameroonian passport, CNI card, and driver license PSD files.",
    "status": "active",
    "display_order": 53,
    "keywords": [
      "cameroon",
      "cm",
      "cameroonian"
    ],
    "aliases": [
      "cm",
      "cameroonian",
      "cmr"
    ]
  },
  {
    "id": "eg",
    "name": "Egypt",
    "slug": "egypt",
    "iso_code": "eg",
    "flag": "https://flagcdn.com/w160/eg.png",
    "description": "Egyptian National ID card, passport, and driver license PSD templates.",
    "seo_title": "Egypt National ID & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Egyptian National ID card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 54,
    "keywords": [
      "egypt",
      "eg",
      "egyptian",
      "cairo"
    ],
    "aliases": [
      "eg",
      "egyptian",
      "egy"
    ]
  },
  {
    "id": "ke",
    "name": "Kenya",
    "slug": "kenya",
    "iso_code": "ke",
    "flag": "https://flagcdn.com/w160/ke.png",
    "description": "Kenyan National ID card, passport, and driving license PSD templates.",
    "seo_title": "Kenya National ID & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Kenyan National ID, passport, and driving license PSD files.",
    "status": "active",
    "display_order": 55,
    "keywords": [
      "kenya",
      "ke",
      "kenyan",
      "nairobi"
    ],
    "aliases": [
      "ke",
      "kenyan",
      "ken"
    ]
  },
  {
    "id": "ma",
    "name": "Morocco",
    "slug": "morocco",
    "iso_code": "ma",
    "flag": "https://flagcdn.com/w160/ma.png",
    "description": "Moroccan CNIE national ID card, passport, and permis de conduire PSD templates.",
    "seo_title": "Morocco CNIE ID & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Moroccan CNIE identity card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 56,
    "keywords": [
      "morocco",
      "ma",
      "moroccan",
      "cnie",
      "rabat"
    ],
    "aliases": [
      "ma",
      "moroccan",
      "mar"
    ]
  },
  {
    "id": "tn",
    "name": "Tunisia",
    "slug": "tunisia",
    "iso_code": "tn",
    "flag": "https://flagcdn.com/w160/tn.png",
    "description": "Tunisian CIN card, passport, and driver license PSD templates.",
    "seo_title": "Tunisia Passport & CIN ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Tunisian CIN identity card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 57,
    "keywords": [
      "tunisia",
      "tn",
      "tunisian",
      "tunis"
    ],
    "aliases": [
      "tn",
      "tunisian",
      "tun"
    ]
  },
  {
    "id": "bh",
    "name": "Bahrain",
    "slug": "bahrain",
    "iso_code": "bh",
    "flag": "https://flagcdn.com/w160/bh.png",
    "description": "Bahraini CPR Smart Card, passport, and driver license PSD templates.",
    "seo_title": "Bahrain CPR Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Bahraini CPR smart card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 58,
    "keywords": [
      "bahrain",
      "bh",
      "bahraini",
      "cpr",
      "manama"
    ],
    "aliases": [
      "bh",
      "bahraini",
      "bhr"
    ]
  },
  {
    "id": "id",
    "name": "Indonesia",
    "slug": "indonesia",
    "iso_code": "id",
    "flag": "https://flagcdn.com/w160/id.png",
    "description": "Indonesian e-KTP card, passport, and SIM driver license PSD templates.",
    "seo_title": "Indonesia e-KTP & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Indonesian e-KTP identity card, passport, and SIM driver license PSD templates.",
    "status": "active",
    "display_order": 59,
    "keywords": [
      "indonesia",
      "id",
      "indonesian",
      "ktp",
      "sim",
      "jakarta"
    ],
    "aliases": [
      "id",
      "indonesian",
      "idn"
    ]
  },
  {
    "id": "iq",
    "name": "Iraq",
    "slug": "iraq",
    "iso_code": "iq",
    "flag": "https://flagcdn.com/w160/iq.png",
    "description": "Iraqi Unified National ID card, passport, and driver license PSD templates.",
    "seo_title": "Iraq National ID & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Iraqi Unified ID card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 60,
    "keywords": [
      "iraq",
      "iq",
      "iraqi",
      "baghdad"
    ],
    "aliases": [
      "iq",
      "iraqi",
      "irq"
    ]
  },
  {
    "id": "jo",
    "name": "Jordan",
    "slug": "jordan",
    "iso_code": "jo",
    "flag": "https://flagcdn.com/w160/jo.png",
    "description": "Jordanian Smart ID card, passport, and driver license PSD templates.",
    "seo_title": "Jordan Passport & Smart ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Jordanian Smart ID card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 61,
    "keywords": [
      "jordan",
      "jo",
      "jordanian",
      "amman"
    ],
    "aliases": [
      "jo",
      "jordanian",
      "jor"
    ]
  },
  {
    "id": "kw",
    "name": "Kuwait",
    "slug": "kuwait",
    "iso_code": "kw",
    "flag": "https://flagcdn.com/w160/kw.png",
    "description": "Kuwait Civil ID card, passport, and driver license PSD templates.",
    "seo_title": "Kuwait Civil ID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Kuwait Civil ID card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 62,
    "keywords": [
      "kuwait",
      "kw",
      "kuwaiti",
      "civil id"
    ],
    "aliases": [
      "kw",
      "kuwaiti",
      "kwt"
    ]
  },
  {
    "id": "my",
    "name": "Malaysia",
    "slug": "malaysia",
    "iso_code": "my",
    "flag": "https://flagcdn.com/w160/my.png",
    "description": "Malaysian MyKad identity card, passport, and driving licence PSD templates.",
    "seo_title": "Malaysia MyKad ID & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Malaysian MyKad card, passport, and driving licence PSD files.",
    "status": "active",
    "display_order": 63,
    "keywords": [
      "malaysia",
      "my",
      "malaysian",
      "mykad",
      "kuala lumpur"
    ],
    "aliases": [
      "my",
      "malaysian",
      "mys"
    ]
  },
  {
    "id": "om",
    "name": "Oman",
    "slug": "oman",
    "iso_code": "om",
    "flag": "https://flagcdn.com/w160/om.png",
    "description": "Omani Resident / Civil ID card, passport, and driver license PSD templates.",
    "seo_title": "Oman Civil ID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Omani Civil ID card, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 64,
    "keywords": [
      "oman",
      "om",
      "omani",
      "muscat"
    ],
    "aliases": [
      "om",
      "omani",
      "omn"
    ]
  },
  {
    "id": "qa",
    "name": "Qatar",
    "slug": "qatar",
    "iso_code": "qa",
    "flag": "https://flagcdn.com/w160/qa.png",
    "description": "Qatari Residence Permit (QID), passport, and driver license PSD templates.",
    "seo_title": "Qatar QID Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Qatari Residence Card (QID), passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 65,
    "keywords": [
      "qatar",
      "qa",
      "qatari",
      "qid",
      "doha"
    ],
    "aliases": [
      "qa",
      "qatari",
      "qat"
    ]
  },
  {
    "id": "sa",
    "name": "Saudi Arabia",
    "slug": "saudi-arabia",
    "iso_code": "sa",
    "flag": "https://flagcdn.com/w160/sa.png",
    "description": "Saudi National ID, Iqama resident card, passport, and driver license PSD templates.",
    "seo_title": "Saudi Arabia Iqama & National ID PSD Templates | RH Editography",
    "seo_description": "Editable Saudi Arabia Iqama card, National ID, passport, and driver license PSD templates.",
    "status": "active",
    "display_order": 66,
    "keywords": [
      "saudi arabia",
      "sa",
      "saudi",
      "iqama",
      "riyadh"
    ],
    "aliases": [
      "sa",
      "saudi",
      "saudi arabia",
      "sau"
    ]
  },
  {
    "id": "sy",
    "name": "Syria",
    "slug": "syria",
    "iso_code": "sy",
    "flag": "https://flagcdn.com/w160/sy.png",
    "description": "Syrian National ID card, passport, and driver license PSD templates.",
    "seo_title": "Syria Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Syrian passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 67,
    "keywords": [
      "syria",
      "sy",
      "syrian",
      "damascus"
    ],
    "aliases": [
      "sy",
      "syrian",
      "syr"
    ]
  },
  {
    "id": "th",
    "name": "Thailand",
    "slug": "thailand",
    "iso_code": "th",
    "flag": "https://flagcdn.com/w160/th.png",
    "description": "Thai National ID card, passport, and driver license PSD templates.",
    "seo_title": "Thailand National ID & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Thai National ID card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 68,
    "keywords": [
      "thailand",
      "th",
      "thai",
      "bangkok"
    ],
    "aliases": [
      "th",
      "thai",
      "tha"
    ]
  },
  {
    "id": "vn",
    "name": "Vietnam",
    "slug": "vietnam",
    "iso_code": "vn",
    "flag": "https://flagcdn.com/w160/vn.png",
    "description": "Vietnamese Citizen Identity Card (CCCD), passport, and driver license PSD templates.",
    "seo_title": "Vietnam CCCD Identity Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Vietnamese CCCD card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 69,
    "keywords": [
      "vietnam",
      "vn",
      "vietnamese",
      "cccd",
      "hanoi"
    ],
    "aliases": [
      "vn",
      "vietnamese",
      "vnm"
    ]
  },
  {
    "id": "ye",
    "name": "Yemen",
    "slug": "yemen",
    "iso_code": "ye",
    "flag": "https://flagcdn.com/w160/ye.png",
    "description": "Yemeni National ID card, passport, and driver license PSD templates.",
    "seo_title": "Yemen Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Yemeni passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 70,
    "keywords": [
      "yemen",
      "ye",
      "yemeni"
    ],
    "aliases": [
      "ye",
      "yemeni",
      "yem"
    ]
  },
  {
    "id": "uz",
    "name": "Uzbekistan",
    "slug": "uzbekistan",
    "iso_code": "uz",
    "flag": "https://flagcdn.com/w160/uz.png",
    "description": "Uzbek ID card, biometric passport, and driver license PSD templates.",
    "seo_title": "Uzbekistan Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Uzbekistan passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 71,
    "keywords": [
      "uzbekistan",
      "uz",
      "uzbek",
      "tashkent"
    ],
    "aliases": [
      "uz",
      "uzbek",
      "uzb"
    ]
  },
  {
    "id": "kg",
    "name": "Kyrgyzstan",
    "slug": "kyrgyzstan",
    "iso_code": "kg",
    "flag": "https://flagcdn.com/w160/kg.png",
    "description": "Kyrgyz national eID card, passport, and driver license PSD templates.",
    "seo_title": "Kyrgyzstan Passport & eID Card PSD Templates | RH Editography",
    "seo_description": "Editable Kyrgyz eID card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 72,
    "keywords": [
      "kyrgyzstan",
      "kg",
      "kyrgyz",
      "bishkek"
    ],
    "aliases": [
      "kg",
      "kyrgyz",
      "kgz"
    ]
  },
  {
    "id": "tm",
    "name": "Turkmenistan",
    "slug": "turkmenistan",
    "iso_code": "tm",
    "flag": "https://flagcdn.com/w160/tm.png",
    "description": "Turkmen passport, ID card, and driver license PSD templates.",
    "seo_title": "Turkmenistan Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Editable Turkmen passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 73,
    "keywords": [
      "turkmenistan",
      "tm",
      "turkmen",
      "ashgabat"
    ],
    "aliases": [
      "tm",
      "turkmen",
      "tkm"
    ]
  },
  {
    "id": "hk",
    "name": "Hong Kong",
    "slug": "hong-kong",
    "iso_code": "hk",
    "flag": "https://flagcdn.com/w160/hk.png",
    "description": "Hong Kong Smart Identity Card (HKID), passport, and driving licence PSD templates.",
    "seo_title": "Hong Kong HKID Smart Card & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Hong Kong HKID smart identity card, passport, and driving licence PSD templates.",
    "status": "active",
    "display_order": 74,
    "keywords": [
      "hong kong",
      "hk",
      "hkid",
      "smart card"
    ],
    "aliases": [
      "hk",
      "hong kong",
      "hkg"
    ]
  },
  {
    "id": "do",
    "name": "Dominican Republic",
    "slug": "dominican-republic",
    "iso_code": "do",
    "flag": "https://flagcdn.com/w160/do.png",
    "description": "Dominican Cedula de Identidad y Electoral, passport, and driver license PSD templates.",
    "seo_title": "Dominican Republic Cedula & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Dominican Republic Cedula card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 75,
    "keywords": [
      "dominican republic",
      "do",
      "dominican",
      "cedula"
    ],
    "aliases": [
      "do",
      "dominican republic",
      "dom"
    ]
  },
  {
    "id": "ve",
    "name": "Venezuela",
    "slug": "venezuela",
    "iso_code": "ve",
    "flag": "https://flagcdn.com/w160/ve.png",
    "description": "Venezuelan Cedula de Identidad, passport, and Licencia de Conducir PSD templates.",
    "seo_title": "Venezuela Cedula & Passport PSD Templates | RH Editography",
    "seo_description": "Editable Venezuelan Cedula ID card, passport, and driver license PSD files.",
    "status": "active",
    "display_order": 76,
    "keywords": [
      "venezuela",
      "ve",
      "venezuelan",
      "cedula",
      "caracas"
    ],
    "aliases": [
      "ve",
      "venezuelan",
      "ven"
    ]
  },
  {
    "id": "rs",
    "name": "Serbia",
    "slug": "serbia",
    "iso_code": "rs",
    "flag": "https://flagcdn.com/w160/rs.png",
    "description": "Serbian Licna karta ID card, passport, and Vozacka dozvola PSD templates.",
    "seo_title": "Serbia Passport & Licna Karta PSD Templates | RH Editography",
    "seo_description": "Editable Serbian passport, ID card, and driver license PSD files.",
    "status": "active",
    "display_order": 77,
    "keywords": [
      "serbia",
      "rs",
      "serbian",
      "belgrade"
    ],
    "aliases": [
      "rs",
      "serbian",
      "srb"
    ]
  },
  {
    "id": "mt",
    "name": "Malta",
    "slug": "malta",
    "iso_code": "mt",
    "flag": "https://flagcdn.com/w160/mt.png",
    "description": "Maltese eID card, passport, and driving licence PSD templates.",
    "seo_title": "Malta Passport & eID Card PSD Templates | RH Editography",
    "seo_description": "Editable Maltese passport, eID card, and driving licence PSD files.",
    "status": "active",
    "display_order": 78,
    "keywords": [
      "malta",
      "mt",
      "maltese",
      "valletta"
    ],
    "aliases": [
      "mt",
      "maltese",
      "mlt"
    ]
  },
  {
    "id": "by",
    "name": "Belarus",
    "slug": "belarus",
    "iso_code": "by",
    "flag": "https://flagcdn.com/w160/by.png",
    "description": "Belarusian passport, ID card, and driver license editable PSD templates.",
    "seo_title": "Belarus Passport & ID Card PSD Templates | RH Editography",
    "seo_description": "Download editable Belarusian passport, ID card, and driver license PSD templates.",
    "status": "active",
    "display_order": 79,
    "keywords": [
      "belarus",
      "by",
      "belarusian",
      "minsk"
    ],
    "aliases": [
      "by",
      "belarusian",
      "blr"
    ]
  },
  {
    "id": "af",
    "name": "Afghanistan",
    "slug": "afghanistan",
    "iso_code": "AF",
    "flag": "🇦🇫",
    "description": "Official Afghanistan PSD templates and documents.",
    "seo_title": "Afghanistan PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Afghanistan. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 89,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "afghanistan",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "afghanistan",
      "af"
    ]
  },
  {
    "id": "al",
    "name": "Albania",
    "slug": "albania",
    "iso_code": "AL",
    "flag": "🇦🇱",
    "description": "Official Albania PSD templates and documents.",
    "seo_title": "Albania PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Albania. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 99,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "albania",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "albania",
      "al"
    ]
  },
  {
    "id": "dz",
    "name": "Algeria",
    "slug": "algeria",
    "iso_code": "DZ",
    "flag": "🇩🇿",
    "description": "Official Algeria PSD templates and documents.",
    "seo_title": "Algeria PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Algeria. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 109,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "algeria",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "algeria",
      "dz"
    ]
  },
  {
    "id": "ad",
    "name": "Andorra",
    "slug": "andorra",
    "iso_code": "AD",
    "flag": "🇦🇩",
    "description": "Official Andorra PSD templates and documents.",
    "seo_title": "Andorra PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Andorra. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 119,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "andorra",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "andorra",
      "ad"
    ]
  },
  {
    "id": "ao",
    "name": "Angola",
    "slug": "angola",
    "iso_code": "AO",
    "flag": "🇦🇴",
    "description": "Official Angola PSD templates and documents.",
    "seo_title": "Angola PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Angola. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 129,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "angola",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "angola",
      "ao"
    ]
  },
  {
    "id": "ag",
    "name": "Antigua and Barbuda",
    "slug": "antigua-and-barbuda",
    "iso_code": "AG",
    "flag": "🇦🇬",
    "description": "Official Antigua and Barbuda PSD templates and documents.",
    "seo_title": "Antigua and Barbuda PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Antigua and Barbuda. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 139,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "antigua and barbuda",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "antigua and barbuda",
      "ag"
    ]
  },
  {
    "id": "bs",
    "name": "Bahamas",
    "slug": "bahamas",
    "iso_code": "BS",
    "flag": "🇧🇸",
    "description": "Official Bahamas PSD templates and documents.",
    "seo_title": "Bahamas PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Bahamas. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 149,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "bahamas",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "bahamas",
      "bs"
    ]
  },
  {
    "id": "bb",
    "name": "Barbados",
    "slug": "barbados",
    "iso_code": "BB",
    "flag": "🇧🇧",
    "description": "Official Barbados PSD templates and documents.",
    "seo_title": "Barbados PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Barbados. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 159,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "barbados",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "barbados",
      "bb"
    ]
  },
  {
    "id": "bz",
    "name": "Belize",
    "slug": "belize",
    "iso_code": "BZ",
    "flag": "🇧🇿",
    "description": "Official Belize PSD templates and documents.",
    "seo_title": "Belize PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Belize. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 169,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "belize",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "belize",
      "bz"
    ]
  },
  {
    "id": "bj",
    "name": "Benin",
    "slug": "benin",
    "iso_code": "BJ",
    "flag": "🇧🇯",
    "description": "Official Benin PSD templates and documents.",
    "seo_title": "Benin PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Benin. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 179,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "benin",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "benin",
      "bj"
    ]
  },
  {
    "id": "bt",
    "name": "Bhutan",
    "slug": "bhutan",
    "iso_code": "BT",
    "flag": "🇧🇹",
    "description": "Official Bhutan PSD templates and documents.",
    "seo_title": "Bhutan PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Bhutan. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 189,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "bhutan",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "bhutan",
      "bt"
    ]
  },
  {
    "id": "bo",
    "name": "Bolivia",
    "slug": "bolivia",
    "iso_code": "BO",
    "flag": "🇧🇴",
    "description": "Official Bolivia PSD templates and documents.",
    "seo_title": "Bolivia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Bolivia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 199,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "bolivia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "bolivia",
      "bo"
    ]
  },
  {
    "id": "ba",
    "name": "Bosnia and Herzegovina",
    "slug": "bosnia-and-herzegovina",
    "iso_code": "BA",
    "flag": "🇧🇦",
    "description": "Official Bosnia and Herzegovina PSD templates and documents.",
    "seo_title": "Bosnia and Herzegovina PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Bosnia and Herzegovina. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 209,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "bosnia and herzegovina",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "bosnia and herzegovina",
      "ba"
    ]
  },
  {
    "id": "bw",
    "name": "Botswana",
    "slug": "botswana",
    "iso_code": "BW",
    "flag": "🇧🇼",
    "description": "Official Botswana PSD templates and documents.",
    "seo_title": "Botswana PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Botswana. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 219,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "botswana",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "botswana",
      "bw"
    ]
  },
  {
    "id": "bn",
    "name": "Brunei",
    "slug": "brunei",
    "iso_code": "BN",
    "flag": "🇧🇳",
    "description": "Official Brunei PSD templates and documents.",
    "seo_title": "Brunei PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Brunei. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 229,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "brunei",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "brunei",
      "bn"
    ]
  },
  {
    "id": "bf",
    "name": "Burkina Faso",
    "slug": "burkina-faso",
    "iso_code": "BF",
    "flag": "🇧🇫",
    "description": "Official Burkina Faso PSD templates and documents.",
    "seo_title": "Burkina Faso PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Burkina Faso. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 239,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "burkina faso",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "burkina faso",
      "bf"
    ]
  },
  {
    "id": "bi",
    "name": "Burundi",
    "slug": "burundi",
    "iso_code": "BI",
    "flag": "🇧🇮",
    "description": "Official Burundi PSD templates and documents.",
    "seo_title": "Burundi PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Burundi. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 249,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "burundi",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "burundi",
      "bi"
    ]
  },
  {
    "id": "cv",
    "name": "Cabo Verde",
    "slug": "cabo-verde",
    "iso_code": "CV",
    "flag": "🇨🇻",
    "description": "Official Cabo Verde PSD templates and documents.",
    "seo_title": "Cabo Verde PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Cabo Verde. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 259,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "cabo verde",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "cabo verde",
      "cv"
    ]
  },
  {
    "id": "kh",
    "name": "Cambodia",
    "slug": "cambodia",
    "iso_code": "KH",
    "flag": "🇰🇭",
    "description": "Official Cambodia PSD templates and documents.",
    "seo_title": "Cambodia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Cambodia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 269,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "cambodia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "cambodia",
      "kh"
    ]
  },
  {
    "id": "cf",
    "name": "Central African Republic",
    "slug": "central-african-republic",
    "iso_code": "CF",
    "flag": "🇨🇫",
    "description": "Official Central African Republic PSD templates and documents.",
    "seo_title": "Central African Republic PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Central African Republic. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 279,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "central african republic",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "central african republic",
      "cf"
    ]
  },
  {
    "id": "td",
    "name": "Chad",
    "slug": "chad",
    "iso_code": "TD",
    "flag": "🇹🇩",
    "description": "Official Chad PSD templates and documents.",
    "seo_title": "Chad PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Chad. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 289,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "chad",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "chad",
      "td"
    ]
  },
  {
    "id": "cl",
    "name": "Chile",
    "slug": "chile",
    "iso_code": "CL",
    "flag": "🇨🇱",
    "description": "Official Chile PSD templates and documents.",
    "seo_title": "Chile PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Chile. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 299,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "chile",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "chile",
      "cl"
    ]
  },
  {
    "id": "co",
    "name": "Colombia",
    "slug": "colombia",
    "iso_code": "CO",
    "flag": "🇨🇴",
    "description": "Official Colombia PSD templates and documents.",
    "seo_title": "Colombia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Colombia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 309,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "colombia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "colombia",
      "co"
    ]
  },
  {
    "id": "km",
    "name": "Comoros",
    "slug": "comoros",
    "iso_code": "KM",
    "flag": "🇰🇲",
    "description": "Official Comoros PSD templates and documents.",
    "seo_title": "Comoros PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Comoros. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 319,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "comoros",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "comoros",
      "km"
    ]
  },
  {
    "id": "cg",
    "name": "Congo (Brazzaville)",
    "slug": "congo-brazzaville",
    "iso_code": "CG",
    "flag": "🇨🇬",
    "description": "Official Congo (Brazzaville) PSD templates and documents.",
    "seo_title": "Congo (Brazzaville) PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Congo (Brazzaville). Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 329,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "congo (brazzaville)",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "congo (brazzaville)",
      "cg"
    ]
  },
  {
    "id": "cd",
    "name": "Congo (Kinshasa)",
    "slug": "congo-kinshasa",
    "iso_code": "CD",
    "flag": "🇨🇩",
    "description": "Official Congo (Kinshasa) PSD templates and documents.",
    "seo_title": "Congo (Kinshasa) PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Congo (Kinshasa). Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 339,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "congo (kinshasa)",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "congo (kinshasa)",
      "cd"
    ]
  },
  {
    "id": "cr",
    "name": "Costa Rica",
    "slug": "costa-rica",
    "iso_code": "CR",
    "flag": "🇨🇷",
    "description": "Official Costa Rica PSD templates and documents.",
    "seo_title": "Costa Rica PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Costa Rica. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 349,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "costa rica",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "costa rica",
      "cr"
    ]
  },
  {
    "id": "cu",
    "name": "Cuba",
    "slug": "cuba",
    "iso_code": "CU",
    "flag": "🇨🇺",
    "description": "Official Cuba PSD templates and documents.",
    "seo_title": "Cuba PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Cuba. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 359,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "cuba",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "cuba",
      "cu"
    ]
  },
  {
    "id": "dj",
    "name": "Djibouti",
    "slug": "djibouti",
    "iso_code": "DJ",
    "flag": "🇩🇯",
    "description": "Official Djibouti PSD templates and documents.",
    "seo_title": "Djibouti PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Djibouti. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 369,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "djibouti",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "djibouti",
      "dj"
    ]
  },
  {
    "id": "dm",
    "name": "Dominica",
    "slug": "dominica",
    "iso_code": "DM",
    "flag": "🇩🇲",
    "description": "Official Dominica PSD templates and documents.",
    "seo_title": "Dominica PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Dominica. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 379,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "dominica",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "dominica",
      "dm"
    ]
  },
  {
    "id": "ec",
    "name": "Ecuador",
    "slug": "ecuador",
    "iso_code": "EC",
    "flag": "🇪🇨",
    "description": "Official Ecuador PSD templates and documents.",
    "seo_title": "Ecuador PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Ecuador. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 389,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "ecuador",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "ecuador",
      "ec"
    ]
  },
  {
    "id": "sv",
    "name": "El Salvador",
    "slug": "el-salvador",
    "iso_code": "SV",
    "flag": "🇸🇻",
    "description": "Official El Salvador PSD templates and documents.",
    "seo_title": "El Salvador PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for El Salvador. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 399,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "el salvador",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "el salvador",
      "sv"
    ]
  },
  {
    "id": "gq",
    "name": "Equatorial Guinea",
    "slug": "equatorial-guinea",
    "iso_code": "GQ",
    "flag": "🇬🇶",
    "description": "Official Equatorial Guinea PSD templates and documents.",
    "seo_title": "Equatorial Guinea PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Equatorial Guinea. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 409,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "equatorial guinea",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "equatorial guinea",
      "gq"
    ]
  },
  {
    "id": "er",
    "name": "Eritrea",
    "slug": "eritrea",
    "iso_code": "ER",
    "flag": "🇪🇷",
    "description": "Official Eritrea PSD templates and documents.",
    "seo_title": "Eritrea PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Eritrea. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 419,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "eritrea",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "eritrea",
      "er"
    ]
  },
  {
    "id": "sz",
    "name": "Eswatini",
    "slug": "eswatini",
    "iso_code": "SZ",
    "flag": "🇸🇿",
    "description": "Official Eswatini PSD templates and documents.",
    "seo_title": "Eswatini PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Eswatini. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 429,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "eswatini",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "eswatini",
      "sz"
    ]
  },
  {
    "id": "et",
    "name": "Ethiopia",
    "slug": "ethiopia",
    "iso_code": "ET",
    "flag": "🇪🇹",
    "description": "Official Ethiopia PSD templates and documents.",
    "seo_title": "Ethiopia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Ethiopia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 439,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "ethiopia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "ethiopia",
      "et"
    ]
  },
  {
    "id": "fj",
    "name": "Fiji",
    "slug": "fiji",
    "iso_code": "FJ",
    "flag": "🇫🇯",
    "description": "Official Fiji PSD templates and documents.",
    "seo_title": "Fiji PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Fiji. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 449,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "fiji",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "fiji",
      "fj"
    ]
  },
  {
    "id": "ga",
    "name": "Gabon",
    "slug": "gabon",
    "iso_code": "GA",
    "flag": "🇬🇦",
    "description": "Official Gabon PSD templates and documents.",
    "seo_title": "Gabon PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Gabon. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 459,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "gabon",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "gabon",
      "ga"
    ]
  },
  {
    "id": "gm",
    "name": "Gambia",
    "slug": "gambia",
    "iso_code": "GM",
    "flag": "🇬🇲",
    "description": "Official Gambia PSD templates and documents.",
    "seo_title": "Gambia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Gambia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 469,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "gambia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "gambia",
      "gm"
    ]
  },
  {
    "id": "gh",
    "name": "Ghana",
    "slug": "ghana",
    "iso_code": "GH",
    "flag": "🇬🇭",
    "description": "Official Ghana PSD templates and documents.",
    "seo_title": "Ghana PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Ghana. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 479,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "ghana",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "ghana",
      "gh"
    ]
  },
  {
    "id": "gd",
    "name": "Grenada",
    "slug": "grenada",
    "iso_code": "GD",
    "flag": "🇬🇩",
    "description": "Official Grenada PSD templates and documents.",
    "seo_title": "Grenada PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Grenada. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 489,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "grenada",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "grenada",
      "gd"
    ]
  },
  {
    "id": "gt",
    "name": "Guatemala",
    "slug": "guatemala",
    "iso_code": "GT",
    "flag": "🇬🇹",
    "description": "Official Guatemala PSD templates and documents.",
    "seo_title": "Guatemala PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Guatemala. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 499,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "guatemala",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "guatemala",
      "gt"
    ]
  },
  {
    "id": "gn",
    "name": "Guinea",
    "slug": "guinea",
    "iso_code": "GN",
    "flag": "🇬🇳",
    "description": "Official Guinea PSD templates and documents.",
    "seo_title": "Guinea PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Guinea. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 509,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "guinea",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "guinea",
      "gn"
    ]
  },
  {
    "id": "gw",
    "name": "Guinea-Bissau",
    "slug": "guinea-bissau",
    "iso_code": "GW",
    "flag": "🇬🇼",
    "description": "Official Guinea-Bissau PSD templates and documents.",
    "seo_title": "Guinea-Bissau PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Guinea-Bissau. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 519,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "guinea-bissau",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "guinea-bissau",
      "gw"
    ]
  },
  {
    "id": "gy",
    "name": "Guyana",
    "slug": "guyana",
    "iso_code": "GY",
    "flag": "🇬🇾",
    "description": "Official Guyana PSD templates and documents.",
    "seo_title": "Guyana PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Guyana. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 529,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "guyana",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "guyana",
      "gy"
    ]
  },
  {
    "id": "ht",
    "name": "Haiti",
    "slug": "haiti",
    "iso_code": "HT",
    "flag": "🇭🇹",
    "description": "Official Haiti PSD templates and documents.",
    "seo_title": "Haiti PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Haiti. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 539,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "haiti",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "haiti",
      "ht"
    ]
  },
  {
    "id": "hn",
    "name": "Honduras",
    "slug": "honduras",
    "iso_code": "HN",
    "flag": "🇭🇳",
    "description": "Official Honduras PSD templates and documents.",
    "seo_title": "Honduras PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Honduras. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 549,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "honduras",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "honduras",
      "hn"
    ]
  },
  {
    "id": "is",
    "name": "Iceland",
    "slug": "iceland",
    "iso_code": "IS",
    "flag": "🇮🇸",
    "description": "Official Iceland PSD templates and documents.",
    "seo_title": "Iceland PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Iceland. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 559,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "iceland",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "iceland",
      "is"
    ]
  },
  {
    "id": "ir",
    "name": "Iran",
    "slug": "iran",
    "iso_code": "IR",
    "flag": "🇮🇷",
    "description": "Official Iran PSD templates and documents.",
    "seo_title": "Iran PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Iran. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 569,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "iran",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "iran",
      "ir"
    ]
  },
  {
    "id": "il",
    "name": "Israel",
    "slug": "israel",
    "iso_code": "IL",
    "flag": "🇮🇱",
    "description": "Official Israel PSD templates and documents.",
    "seo_title": "Israel PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Israel. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 579,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "israel",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "israel",
      "il"
    ]
  },
  {
    "id": "jm",
    "name": "Jamaica",
    "slug": "jamaica",
    "iso_code": "JM",
    "flag": "🇯🇲",
    "description": "Official Jamaica PSD templates and documents.",
    "seo_title": "Jamaica PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Jamaica. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 589,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "jamaica",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "jamaica",
      "jm"
    ]
  },
  {
    "id": "ki",
    "name": "Kiribati",
    "slug": "kiribati",
    "iso_code": "KI",
    "flag": "🇰🇮",
    "description": "Official Kiribati PSD templates and documents.",
    "seo_title": "Kiribati PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Kiribati. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 599,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "kiribati",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "kiribati",
      "ki"
    ]
  },
  {
    "id": "la",
    "name": "Laos",
    "slug": "laos",
    "iso_code": "LA",
    "flag": "🇱🇦",
    "description": "Official Laos PSD templates and documents.",
    "seo_title": "Laos PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Laos. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 609,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "laos",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "laos",
      "la"
    ]
  },
  {
    "id": "lb",
    "name": "Lebanon",
    "slug": "lebanon",
    "iso_code": "LB",
    "flag": "🇱🇧",
    "description": "Official Lebanon PSD templates and documents.",
    "seo_title": "Lebanon PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Lebanon. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 619,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "lebanon",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "lebanon",
      "lb"
    ]
  },
  {
    "id": "ls",
    "name": "Lesotho",
    "slug": "lesotho",
    "iso_code": "LS",
    "flag": "🇱🇸",
    "description": "Official Lesotho PSD templates and documents.",
    "seo_title": "Lesotho PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Lesotho. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 629,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "lesotho",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "lesotho",
      "ls"
    ]
  },
  {
    "id": "lr",
    "name": "Liberia",
    "slug": "liberia",
    "iso_code": "LR",
    "flag": "🇱🇷",
    "description": "Official Liberia PSD templates and documents.",
    "seo_title": "Liberia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Liberia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 639,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "liberia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "liberia",
      "lr"
    ]
  },
  {
    "id": "ly",
    "name": "Libya",
    "slug": "libya",
    "iso_code": "LY",
    "flag": "🇱🇾",
    "description": "Official Libya PSD templates and documents.",
    "seo_title": "Libya PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Libya. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 649,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "libya",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "libya",
      "ly"
    ]
  },
  {
    "id": "li",
    "name": "Liechtenstein",
    "slug": "liechtenstein",
    "iso_code": "LI",
    "flag": "🇱🇮",
    "description": "Official Liechtenstein PSD templates and documents.",
    "seo_title": "Liechtenstein PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Liechtenstein. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 659,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "liechtenstein",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "liechtenstein",
      "li"
    ]
  },
  {
    "id": "lu",
    "name": "Luxembourg",
    "slug": "luxembourg",
    "iso_code": "LU",
    "flag": "🇱🇺",
    "description": "Official Luxembourg PSD templates and documents.",
    "seo_title": "Luxembourg PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Luxembourg. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 669,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "luxembourg",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "luxembourg",
      "lu"
    ]
  },
  {
    "id": "mg",
    "name": "Madagascar",
    "slug": "madagascar",
    "iso_code": "MG",
    "flag": "🇲🇬",
    "description": "Official Madagascar PSD templates and documents.",
    "seo_title": "Madagascar PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Madagascar. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 679,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "madagascar",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "madagascar",
      "mg"
    ]
  },
  {
    "id": "mw",
    "name": "Malawi",
    "slug": "malawi",
    "iso_code": "MW",
    "flag": "🇲🇼",
    "description": "Official Malawi PSD templates and documents.",
    "seo_title": "Malawi PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Malawi. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 689,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "malawi",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "malawi",
      "mw"
    ]
  },
  {
    "id": "mv",
    "name": "Maldives",
    "slug": "maldives",
    "iso_code": "MV",
    "flag": "🇲🇻",
    "description": "Official Maldives PSD templates and documents.",
    "seo_title": "Maldives PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Maldives. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 699,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "maldives",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "maldives",
      "mv"
    ]
  },
  {
    "id": "ml",
    "name": "Mali",
    "slug": "mali",
    "iso_code": "ML",
    "flag": "🇲🇱",
    "description": "Official Mali PSD templates and documents.",
    "seo_title": "Mali PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Mali. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 709,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "mali",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "mali",
      "ml"
    ]
  },
  {
    "id": "mh",
    "name": "Marshall Islands",
    "slug": "marshall-islands",
    "iso_code": "MH",
    "flag": "🇲🇭",
    "description": "Official Marshall Islands PSD templates and documents.",
    "seo_title": "Marshall Islands PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Marshall Islands. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 719,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "marshall islands",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "marshall islands",
      "mh"
    ]
  },
  {
    "id": "mr",
    "name": "Mauritania",
    "slug": "mauritania",
    "iso_code": "MR",
    "flag": "🇲🇷",
    "description": "Official Mauritania PSD templates and documents.",
    "seo_title": "Mauritania PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Mauritania. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 729,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "mauritania",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "mauritania",
      "mr"
    ]
  },
  {
    "id": "mu",
    "name": "Mauritius",
    "slug": "mauritius",
    "iso_code": "MU",
    "flag": "🇲🇺",
    "description": "Official Mauritius PSD templates and documents.",
    "seo_title": "Mauritius PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Mauritius. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 739,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "mauritius",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "mauritius",
      "mu"
    ]
  },
  {
    "id": "fm",
    "name": "Micronesia",
    "slug": "micronesia",
    "iso_code": "FM",
    "flag": "🇫🇲",
    "description": "Official Micronesia PSD templates and documents.",
    "seo_title": "Micronesia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Micronesia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 749,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "micronesia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "micronesia",
      "fm"
    ]
  },
  {
    "id": "mc",
    "name": "Monaco",
    "slug": "monaco",
    "iso_code": "MC",
    "flag": "🇲🇨",
    "description": "Official Monaco PSD templates and documents.",
    "seo_title": "Monaco PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Monaco. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 759,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "monaco",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "monaco",
      "mc"
    ]
  },
  {
    "id": "mn",
    "name": "Mongolia",
    "slug": "mongolia",
    "iso_code": "MN",
    "flag": "🇲🇳",
    "description": "Official Mongolia PSD templates and documents.",
    "seo_title": "Mongolia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Mongolia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 769,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "mongolia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "mongolia",
      "mn"
    ]
  },
  {
    "id": "me",
    "name": "Montenegro",
    "slug": "montenegro",
    "iso_code": "ME",
    "flag": "🇲🇪",
    "description": "Official Montenegro PSD templates and documents.",
    "seo_title": "Montenegro PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Montenegro. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 779,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "montenegro",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "montenegro",
      "me"
    ]
  },
  {
    "id": "mz",
    "name": "Mozambique",
    "slug": "mozambique",
    "iso_code": "MZ",
    "flag": "🇲🇿",
    "description": "Official Mozambique PSD templates and documents.",
    "seo_title": "Mozambique PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Mozambique. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 789,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "mozambique",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "mozambique",
      "mz"
    ]
  },
  {
    "id": "mm",
    "name": "Myanmar",
    "slug": "myanmar",
    "iso_code": "MM",
    "flag": "🇲🇲",
    "description": "Official Myanmar PSD templates and documents.",
    "seo_title": "Myanmar PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Myanmar. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 799,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "myanmar",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "myanmar",
      "mm"
    ]
  },
  {
    "id": "na",
    "name": "Namibia",
    "slug": "namibia",
    "iso_code": "NA",
    "flag": "🇳🇦",
    "description": "Official Namibia PSD templates and documents.",
    "seo_title": "Namibia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Namibia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 809,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "namibia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "namibia",
      "na"
    ]
  },
  {
    "id": "nr",
    "name": "Nauru",
    "slug": "nauru",
    "iso_code": "NR",
    "flag": "🇳🇷",
    "description": "Official Nauru PSD templates and documents.",
    "seo_title": "Nauru PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Nauru. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 819,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "nauru",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "nauru",
      "nr"
    ]
  },
  {
    "id": "np",
    "name": "Nepal",
    "slug": "nepal",
    "iso_code": "NP",
    "flag": "🇳🇵",
    "description": "Official Nepal PSD templates and documents.",
    "seo_title": "Nepal PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Nepal. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 829,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "nepal",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "nepal",
      "np"
    ]
  },
  {
    "id": "ni",
    "name": "Nicaragua",
    "slug": "nicaragua",
    "iso_code": "NI",
    "flag": "🇳🇮",
    "description": "Official Nicaragua PSD templates and documents.",
    "seo_title": "Nicaragua PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Nicaragua. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 839,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "nicaragua",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "nicaragua",
      "ni"
    ]
  },
  {
    "id": "ne",
    "name": "Niger",
    "slug": "niger",
    "iso_code": "NE",
    "flag": "🇳🇪",
    "description": "Official Niger PSD templates and documents.",
    "seo_title": "Niger PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Niger. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 849,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "niger",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "niger",
      "ne"
    ]
  },
  {
    "id": "ng",
    "name": "Nigeria",
    "slug": "nigeria",
    "iso_code": "NG",
    "flag": "🇳🇬",
    "description": "Official Nigeria PSD templates and documents.",
    "seo_title": "Nigeria PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Nigeria. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 859,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "nigeria",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "nigeria",
      "ng"
    ]
  },
  {
    "id": "kp",
    "name": "North Korea",
    "slug": "north-korea",
    "iso_code": "KP",
    "flag": "🇰🇵",
    "description": "Official North Korea PSD templates and documents.",
    "seo_title": "North Korea PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for North Korea. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 869,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "north korea",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "north korea",
      "kp"
    ]
  },
  {
    "id": "mk",
    "name": "North Macedonia",
    "slug": "north-macedonia",
    "iso_code": "MK",
    "flag": "🇲🇰",
    "description": "Official North Macedonia PSD templates and documents.",
    "seo_title": "North Macedonia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for North Macedonia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 879,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "north macedonia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "north macedonia",
      "mk"
    ]
  },
  {
    "id": "pw",
    "name": "Palau",
    "slug": "palau",
    "iso_code": "PW",
    "flag": "🇵🇼",
    "description": "Official Palau PSD templates and documents.",
    "seo_title": "Palau PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Palau. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 889,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "palau",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "palau",
      "pw"
    ]
  },
  {
    "id": "ps",
    "name": "Palestine",
    "slug": "palestine",
    "iso_code": "PS",
    "flag": "🇵🇸",
    "description": "Official Palestine PSD templates and documents.",
    "seo_title": "Palestine PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Palestine. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 899,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "palestine",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "palestine",
      "ps"
    ]
  },
  {
    "id": "pa",
    "name": "Panama",
    "slug": "panama",
    "iso_code": "PA",
    "flag": "🇵🇦",
    "description": "Official Panama PSD templates and documents.",
    "seo_title": "Panama PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Panama. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 909,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "panama",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "panama",
      "pa"
    ]
  },
  {
    "id": "pg",
    "name": "Papua New Guinea",
    "slug": "papua-new-guinea",
    "iso_code": "PG",
    "flag": "🇵🇬",
    "description": "Official Papua New Guinea PSD templates and documents.",
    "seo_title": "Papua New Guinea PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Papua New Guinea. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 919,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "papua new guinea",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "papua new guinea",
      "pg"
    ]
  },
  {
    "id": "py",
    "name": "Paraguay",
    "slug": "paraguay",
    "iso_code": "PY",
    "flag": "🇵🇾",
    "description": "Official Paraguay PSD templates and documents.",
    "seo_title": "Paraguay PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Paraguay. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 929,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "paraguay",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "paraguay",
      "py"
    ]
  },
  {
    "id": "pe",
    "name": "Peru",
    "slug": "peru",
    "iso_code": "PE",
    "flag": "🇵🇪",
    "description": "Official Peru PSD templates and documents.",
    "seo_title": "Peru PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Peru. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 939,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "peru",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "peru",
      "pe"
    ]
  },
  {
    "id": "rw",
    "name": "Rwanda",
    "slug": "rwanda",
    "iso_code": "RW",
    "flag": "🇷🇼",
    "description": "Official Rwanda PSD templates and documents.",
    "seo_title": "Rwanda PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Rwanda. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 949,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "rwanda",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "rwanda",
      "rw"
    ]
  },
  {
    "id": "kn",
    "name": "Saint Kitts and Nevis",
    "slug": "saint-kitts-and-nevis",
    "iso_code": "KN",
    "flag": "🇰🇳",
    "description": "Official Saint Kitts and Nevis PSD templates and documents.",
    "seo_title": "Saint Kitts and Nevis PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Saint Kitts and Nevis. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 959,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "saint kitts and nevis",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "saint kitts and nevis",
      "kn"
    ]
  },
  {
    "id": "lc",
    "name": "Saint Lucia",
    "slug": "saint-lucia",
    "iso_code": "LC",
    "flag": "🇱🇨",
    "description": "Official Saint Lucia PSD templates and documents.",
    "seo_title": "Saint Lucia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Saint Lucia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 969,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "saint lucia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "saint lucia",
      "lc"
    ]
  },
  {
    "id": "vc",
    "name": "Saint Vincent and the Grenadines",
    "slug": "saint-vincent-and-the-grenadines",
    "iso_code": "VC",
    "flag": "🇻🇨",
    "description": "Official Saint Vincent and the Grenadines PSD templates and documents.",
    "seo_title": "Saint Vincent and the Grenadines PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Saint Vincent and the Grenadines. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 979,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "saint vincent and the grenadines",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "saint vincent and the grenadines",
      "vc"
    ]
  },
  {
    "id": "ws",
    "name": "Samoa",
    "slug": "samoa",
    "iso_code": "WS",
    "flag": "🇼🇸",
    "description": "Official Samoa PSD templates and documents.",
    "seo_title": "Samoa PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Samoa. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 989,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "samoa",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "samoa",
      "ws"
    ]
  },
  {
    "id": "sm",
    "name": "San Marino",
    "slug": "san-marino",
    "iso_code": "SM",
    "flag": "🇸🇲",
    "description": "Official San Marino PSD templates and documents.",
    "seo_title": "San Marino PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for San Marino. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 999,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "san marino",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "san marino",
      "sm"
    ]
  },
  {
    "id": "st",
    "name": "Sao Tome and Principe",
    "slug": "sao-tome-and-principe",
    "iso_code": "ST",
    "flag": "🇸🇹",
    "description": "Official Sao Tome and Principe PSD templates and documents.",
    "seo_title": "Sao Tome and Principe PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Sao Tome and Principe. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1009,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "sao tome and principe",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "sao tome and principe",
      "st"
    ]
  },
  {
    "id": "sn",
    "name": "Senegal",
    "slug": "senegal",
    "iso_code": "SN",
    "flag": "🇸🇳",
    "description": "Official Senegal PSD templates and documents.",
    "seo_title": "Senegal PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Senegal. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1019,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "senegal",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "senegal",
      "sn"
    ]
  },
  {
    "id": "sc",
    "name": "Seychelles",
    "slug": "seychelles",
    "iso_code": "SC",
    "flag": "🇸🇨",
    "description": "Official Seychelles PSD templates and documents.",
    "seo_title": "Seychelles PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Seychelles. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1029,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "seychelles",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "seychelles",
      "sc"
    ]
  },
  {
    "id": "sl",
    "name": "Sierra Leone",
    "slug": "sierra-leone",
    "iso_code": "SL",
    "flag": "🇸🇱",
    "description": "Official Sierra Leone PSD templates and documents.",
    "seo_title": "Sierra Leone PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Sierra Leone. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1039,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "sierra leone",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "sierra leone",
      "sl"
    ]
  },
  {
    "id": "sb",
    "name": "Solomon Islands",
    "slug": "solomon-islands",
    "iso_code": "SB",
    "flag": "🇸🇧",
    "description": "Official Solomon Islands PSD templates and documents.",
    "seo_title": "Solomon Islands PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Solomon Islands. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1049,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "solomon islands",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "solomon islands",
      "sb"
    ]
  },
  {
    "id": "so",
    "name": "Somalia",
    "slug": "somalia",
    "iso_code": "SO",
    "flag": "🇸🇴",
    "description": "Official Somalia PSD templates and documents.",
    "seo_title": "Somalia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Somalia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1059,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "somalia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "somalia",
      "so"
    ]
  },
  {
    "id": "kr",
    "name": "South Korea",
    "slug": "south-korea",
    "iso_code": "KR",
    "flag": "🇰🇷",
    "description": "Official South Korea PSD templates and documents.",
    "seo_title": "South Korea PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for South Korea. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1069,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "south korea",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "south korea",
      "kr"
    ]
  },
  {
    "id": "ss",
    "name": "South Sudan",
    "slug": "south-sudan",
    "iso_code": "SS",
    "flag": "🇸🇸",
    "description": "Official South Sudan PSD templates and documents.",
    "seo_title": "South Sudan PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for South Sudan. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1079,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "south sudan",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "south sudan",
      "ss"
    ]
  },
  {
    "id": "lk",
    "name": "Sri Lanka",
    "slug": "sri-lanka",
    "iso_code": "LK",
    "flag": "🇱🇰",
    "description": "Official Sri Lanka PSD templates and documents.",
    "seo_title": "Sri Lanka PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Sri Lanka. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1089,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "sri lanka",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "sri lanka",
      "lk"
    ]
  },
  {
    "id": "sd",
    "name": "Sudan",
    "slug": "sudan",
    "iso_code": "SD",
    "flag": "🇸🇩",
    "description": "Official Sudan PSD templates and documents.",
    "seo_title": "Sudan PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Sudan. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1099,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "sudan",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "sudan",
      "sd"
    ]
  },
  {
    "id": "sr",
    "name": "Suriname",
    "slug": "suriname",
    "iso_code": "SR",
    "flag": "🇸🇷",
    "description": "Official Suriname PSD templates and documents.",
    "seo_title": "Suriname PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Suriname. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1109,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "suriname",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "suriname",
      "sr"
    ]
  },
  {
    "id": "tw",
    "name": "Taiwan",
    "slug": "taiwan",
    "iso_code": "TW",
    "flag": "🇹🇼",
    "description": "Official Taiwan PSD templates and documents.",
    "seo_title": "Taiwan PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Taiwan. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1119,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "taiwan",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "taiwan",
      "tw"
    ]
  },
  {
    "id": "tj",
    "name": "Tajikistan",
    "slug": "tajikistan",
    "iso_code": "TJ",
    "flag": "🇹🇯",
    "description": "Official Tajikistan PSD templates and documents.",
    "seo_title": "Tajikistan PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Tajikistan. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1129,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "tajikistan",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "tajikistan",
      "tj"
    ]
  },
  {
    "id": "tz",
    "name": "Tanzania",
    "slug": "tanzania",
    "iso_code": "TZ",
    "flag": "🇹🇿",
    "description": "Official Tanzania PSD templates and documents.",
    "seo_title": "Tanzania PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Tanzania. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1139,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "tanzania",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "tanzania",
      "tz"
    ]
  },
  {
    "id": "tg",
    "name": "Togo",
    "slug": "togo",
    "iso_code": "TG",
    "flag": "🇹🇬",
    "description": "Official Togo PSD templates and documents.",
    "seo_title": "Togo PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Togo. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1149,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "togo",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "togo",
      "tg"
    ]
  },
  {
    "id": "to",
    "name": "Tonga",
    "slug": "tonga",
    "iso_code": "TO",
    "flag": "🇹🇴",
    "description": "Official Tonga PSD templates and documents.",
    "seo_title": "Tonga PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Tonga. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1159,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "tonga",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "tonga",
      "to"
    ]
  },
  {
    "id": "tt",
    "name": "Trinidad and Tobago",
    "slug": "trinidad-and-tobago",
    "iso_code": "TT",
    "flag": "🇹🇹",
    "description": "Official Trinidad and Tobago PSD templates and documents.",
    "seo_title": "Trinidad and Tobago PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Trinidad and Tobago. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1169,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "trinidad and tobago",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "trinidad and tobago",
      "tt"
    ]
  },
  {
    "id": "tv",
    "name": "Tuvalu",
    "slug": "tuvalu",
    "iso_code": "TV",
    "flag": "🇹🇻",
    "description": "Official Tuvalu PSD templates and documents.",
    "seo_title": "Tuvalu PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Tuvalu. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1179,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "tuvalu",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "tuvalu",
      "tv"
    ]
  },
  {
    "id": "ug",
    "name": "Uganda",
    "slug": "uganda",
    "iso_code": "UG",
    "flag": "🇺🇬",
    "description": "Official Uganda PSD templates and documents.",
    "seo_title": "Uganda PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Uganda. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1189,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "uganda",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "uganda",
      "ug"
    ]
  },
  {
    "id": "uy",
    "name": "Uruguay",
    "slug": "uruguay",
    "iso_code": "UY",
    "flag": "🇺🇾",
    "description": "Official Uruguay PSD templates and documents.",
    "seo_title": "Uruguay PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Uruguay. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1199,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "uruguay",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "uruguay",
      "uy"
    ]
  },
  {
    "id": "vu",
    "name": "Vanuatu",
    "slug": "vanuatu",
    "iso_code": "VU",
    "flag": "🇻🇺",
    "description": "Official Vanuatu PSD templates and documents.",
    "seo_title": "Vanuatu PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Vanuatu. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1209,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "vanuatu",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "vanuatu",
      "vu"
    ]
  },
  {
    "id": "va",
    "name": "Vatican City",
    "slug": "vatican-city",
    "iso_code": "VA",
    "flag": "🇻🇦",
    "description": "Official Vatican City PSD templates and documents.",
    "seo_title": "Vatican City PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Vatican City. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1219,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "vatican city",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "vatican city",
      "va"
    ]
  },
  {
    "id": "zm",
    "name": "Zambia",
    "slug": "zambia",
    "iso_code": "ZM",
    "flag": "🇿🇲",
    "description": "Official Zambia PSD templates and documents.",
    "seo_title": "Zambia PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Zambia. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1229,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "zambia",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "zambia",
      "zm"
    ]
  },
  {
    "id": "zw",
    "name": "Zimbabwe",
    "slug": "zimbabwe",
    "iso_code": "ZW",
    "flag": "🇿🇼",
    "description": "Official Zimbabwe PSD templates and documents.",
    "seo_title": "Zimbabwe PSD Templates & Editable Documents | RH Editography",
    "seo_description": "Download premium editable PSD templates for Zimbabwe. Fully customizable, organized layers, and ready for immediate download.",
    "status": "active",
    "display_order": 1239,
    "is_featured": false,
    "productCount": 0,
    "keywords": [
      "zimbabwe",
      "templates",
      "editable psd",
      "documents"
    ],
    "aliases": [
      "zimbabwe",
      "zw"
    ]
  }
];

// Helper: Normalize country search query for smart matching
export function searchCountries<T extends CountryModel>(countries: T[], queryStr: string): T[] {
  if (!queryStr || !queryStr.trim()) return countries;

  const q = queryStr.toLowerCase().trim();
  const tokens = q.split(/\s+/).filter(Boolean);

  return countries.filter(c => {
    const name = (c.name && typeof c.name === 'string') ? c.name.toLowerCase() : '';
    const iso = (c.iso_code && typeof c.iso_code === 'string') ? c.iso_code.toLowerCase() : '';
    const slug = (c.slug && typeof c.slug === 'string') ? c.slug.toLowerCase() : '';

    // 1. Direct name match
    if (name.includes(q)) return true;

    // 2. ISO Code match
    if (iso === q || iso.startsWith(q)) return true;

    // 3. Slug match
    if (slug.includes(q)) return true;

    // 4. Keywords or Aliases
    if (c.keywords && Array.isArray(c.keywords) && c.keywords.some(k => typeof k === 'string' && k.toLowerCase().includes(q))) return true;
    if (c.aliases && Array.isArray(c.aliases) && c.aliases.some(a => typeof a === 'string' && a.toLowerCase().includes(q))) return true;

    // 5. Tokenized / Typo-tolerant partial match
    return tokens.every(token => 
      name.includes(token) ||
      iso.includes(token) ||
      slug.includes(token) ||
      (c.keywords && Array.isArray(c.keywords) && c.keywords.some(k => typeof k === 'string' && k.toLowerCase().includes(token))) ||
      (c.aliases && Array.isArray(c.aliases) && c.aliases.some(a => typeof a === 'string' && a.toLowerCase().includes(token)))
    );
  });
}

// Filter products matching a specific country
export function filterProductsByCountryModel(products: Product[], country: CountryModel | string | null | undefined): Product[] {
  if (!country) return [];
  const countryObj = typeof country === 'string' ? null : country;
  const countryName = typeof country === 'string' ? country : (typeof country.name === 'string' ? country.name : (country.name ? String(country.name) : ''));
  const countryId = (typeof country === 'string' ? '' : (typeof country.id === 'string' ? country.id : (country.id ? String(country.id) : ''))).toLowerCase().trim();
  const countryIso = (typeof country === 'string' ? '' : (typeof country.iso_code === 'string' ? country.iso_code : (country.iso_code ? String(country.iso_code) : ''))).toLowerCase().trim();
  const countrySlug = (typeof country === 'string' ? slugify(country) : (typeof country.slug === 'string' ? country.slug : slugify(countryName))).toLowerCase().trim();

  const normName = countryName.toLowerCase().trim();

  // Create list of valid explicit identifiers for this country
  const explicitIdentifiers = new Set<string>();
  if (normName) explicitIdentifiers.add(normName);
  if (countrySlug) explicitIdentifiers.add(countrySlug);
  if (countryIso) explicitIdentifiers.add(countryIso);

  if (countryObj?.aliases) {
    countryObj.aliases.forEach(a => {
      const aNorm = (a && typeof a === 'string') ? a.toLowerCase().trim() : '';
      if (aNorm && aNorm.length > 1 && aNorm !== 'dl' && aNorm !== 'id' && aNorm !== 'tin') {
        explicitIdentifiers.add(aNorm);
      }
    });
  } else {
    if (normName.includes('united states') || normName === 'usa' || normName === 'us') {
      ['united states', 'usa', 'us', 'american', 'america'].forEach(t => explicitIdentifiers.add(t));
    } else if (normName.includes('united kingdom') || normName === 'uk' || normName === 'gb') {
      ['united kingdom', 'uk', 'gb', 'british', 'england', 'britain'].forEach(t => explicitIdentifiers.add(t));
    } else if (normName.includes('canada') || normName === 'ca') {
      ['canada', 'ca', 'canadian'].forEach(t => explicitIdentifiers.add(t));
    } else if (normName.includes('germany') || normName === 'de') {
      ['germany', 'de', 'german', 'deutschland'].forEach(t => explicitIdentifiers.add(t));
    } else if (normName.includes('australia') || normName === 'au') {
      ['australia', 'au', 'australian', 'aussie'].forEach(t => explicitIdentifiers.add(t));
    }
  }

  const idList = Array.from(explicitIdentifiers);

  return (products || []).filter(p => {
    if (!p) return false;
    const pCountryId = (typeof p.country_id === 'string' ? p.country_id : (p.country_id ? String(p.country_id) : '')).toLowerCase().trim();
    const pCountry = (typeof p.country === 'string' ? p.country : (p.country ? String(p.country) : '')).toLowerCase().trim();
    const pCountrySlug = slugify(pCountry);

    // 1. Direct country_id match (e.g. 'us', 'gb', 'ca', 'bd')
    if (countryId && pCountryId === countryId) return true;
    if (countryIso && pCountryId === countryIso) return true;

    // 2. Direct product.country match against name, slug, iso, or aliases
    if (pCountry) {
      if (pCountry === normName || pCountry === countrySlug || pCountry === countryIso) return true;
      if (pCountrySlug === countrySlug) return true;
      if (idList.some(id => id === pCountry || id === pCountrySlug)) return true;
    }

    // 3. Fallback when product has no country/country_id: check if product title explicitly mentions country full name or primary alias as word
    if (!pCountry && !pCountryId) {
      const titleLower = p.title ? p.title.toLowerCase() : '';
      // Only match full country names or clear primary aliases like USA, UK, Canada
      if (titleLower.includes(normName) || titleLower.includes(countrySlug.replace(/-/g, ' '))) return true;
      if (normName.includes('united states') && (titleLower.includes('usa') || titleLower.includes('us ') || titleLower.includes('american'))) return true;
      if (normName.includes('united kingdom') && (titleLower.includes('uk ') || titleLower.includes('british'))) return true;
    }

    return false;
  });
}

// Compute Country Statistics directly from products & country list
export interface CountryStatistics {
  totalCountries: number;
  countriesWithProducts: number;
  countriesWithoutProducts: number;
  topCountries: { country: CountryModel; count: number }[];
  featuredCountries: CountryModel[];
  newestCountries: CountryModel[];
}

export function computeCountryStatistics(countries: CountryModel[], products: Product[]): CountryStatistics {
  const activeProducts = products.filter(p => !p.status || p.status === 'published' || p.status === 'active');

  const countryCounts = countries.filter(c => c && typeof c === 'object').map(c => {
    const count = filterProductsByCountryModel(activeProducts, c).length;
    return { country: c, count };
  });

  const withProds = countryCounts.filter(cc => cc.count > 0).length;
  const withoutProds = countryCounts.filter(cc => cc.count === 0).length;

  const sortedByCount = [...countryCounts].sort((a, b) => b.count - a.count);
  const topCountries = sortedByCount.slice(0, 8);

  const featuredCountries = countries.filter(c => c && typeof c === 'object' && c.is_featured);
  const newestCountries = [...countries].reverse().slice(0, 8);

  return {
    totalCountries: countries.length,
    countriesWithProducts: withProds,
    countriesWithoutProducts: withoutProds,
    topCountries,
    featuredCountries: featuredCountries.length > 0 ? featuredCountries : countries.slice(0, 6),
    newestCountries
  };
}

// Local caching helper
export function getLocalCachedCountries(): CountryModel[] {
  try {
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem('rh_cached_countries');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    }
  } catch (e) {}
  return INITIAL_COUNTRIES;
}

const countrySubscribers = new Set<(countries: CountryModel[]) => void>();
let activeCountriesUnsub: (() => void) | null = null;

// Realtime Firestore Subscription (Multiplexed Singleton)
export async function fetchCountriesFromFirestore(): Promise<CountryModel[]> {
  const startTime = performance.now();
  try {
    const q = query(collection(db, COUNTRIES_COLLECTION), orderBy('display_order', 'asc'));
    const snapshot = await getDocs(q);
    const duration = performance.now() - startTime;

    if (snapshot.empty) {
      perfTracker.logFetch(COUNTRIES_COLLECTION, duration, 'server', 0);
      return INITIAL_COUNTRIES;
    }

    let items: CountryModel[] = snapshot.docs.map((docSnap) => {
      const data = docSnap.data() as Partial<CountryModel>;
      return {
        id: docSnap.id,
        name: data.name || 'Unknown',
        slug: data.slug || slugify(data.name || 'unknown'),
        iso_code: data.iso_code || 'us',
        flag: data.flag || `https://flagcdn.com/w160/${(data.iso_code || 'us').toLowerCase()}.png`,
        description: data.description || '',
        seo_title: data.seo_title,
        seo_description: data.seo_description,
        status: data.status || 'active',
        display_order: data.display_order || 0,
        is_featured: data.is_featured || false,
        productCount: data.productCount || 0,
        keywords: data.keywords || [],
        aliases: data.aliases || [],
        created_at: data.created_at,
        updated_at: data.updated_at
      };
    });

    // Auto-merge missing countries from INITIAL_COUNTRIES
    const existingIds = new Set(items.map(i => i.id));
    const missing = INITIAL_COUNTRIES.filter(c => !existingIds.has(c.id));
    if (missing.length > 0) {
      items = [...items, ...missing];
      // Note: We don't force save them to Firestore here to keep reads fast, 
      // but they will appear in the UI and can be managed by admin.
    }

    items.sort((a, b) => a.display_order - b.display_order);

    if (typeof window !== 'undefined') {
      localStorage.setItem('rh_cached_countries', JSON.stringify(items));
    }
    
    perfTracker.logFetch(COUNTRIES_COLLECTION, duration, 'server', items.length);
    return items;
  } catch (err) {
    console.warn('Error fetching countries from Firestore:', err);
    return getLocalCachedCountries();
  }
}

export function subscribeToCountriesFromFirestore(onUpdate: (countries: CountryModel[]) => void): () => void {
  onUpdate(getLocalCachedCountries());

  countrySubscribers.add(onUpdate);

  fetchCountriesFromFirestore().then((items) => {
    if (items && items.length > 0) {
      countrySubscribers.forEach((cb) => {
        try { cb(items); } catch (e) {}
      });
    }
  }).catch((err) => {
    console.warn('One-time countries fetch error:', err);
  });

  return () => {
    countrySubscribers.delete(onUpdate);
  };
}

// Save or Update Country in Firestore
export async function saveCountryToFirestore(country: CountryModel): Promise<void> {
  try {
    const countryId = country.id || (country.iso_code ? country.iso_code.toLowerCase() : 'unknown');
    const docData = {
      ...country,
      id: countryId,
      slug: country.slug || slugify(country.name),
      updated_at: new Date().toISOString()
    };

    await setDoc(doc(db, COUNTRIES_COLLECTION, countryId), docData, { merge: true });
  } catch (err) {
    console.error('Error saving country to Firestore:', err);
  }
}

// Delete Country from Firestore
export async function deleteCountryFromFirestore(countryId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, COUNTRIES_COLLECTION, countryId));
  } catch (err) {
    console.error('Error deleting country from Firestore:', err);
  }
}
